<!-- src/components/secrets/form/SecretContentInputArea.vue -->

<script setup lang="ts">
  import { useCharCounter } from '@/composables/useCharCounter';
  import { useTextarea } from '@/composables/useTextarea';
  import { computed, watch } from 'vue';

  const props = withDefaults(
    defineProps<{
      maxLength?: number;
      initialContent?: string;
      cornerClass?: string;
      disabled?: boolean;
      minHeight?: number;
      maxHeight?: number;
    }>(),
    {
      maxLength: 10000,
      disabled: false,
      initialContent: '',
      cornerClass: '',
      minHeight: 200,
      maxHeight: 400,
    }
  );

  const emit = defineEmits(['update:content']);

  const { content, charCount, textareaRef, checkContentLength, clearTextarea } = useTextarea({
    maxLength: props.maxLength || 10000,
    initialContent: props.initialContent,
    maxHeight: props.maxHeight,
    onContentChange: (newContent) => emit('update:content', newContent),
  });

  const { isHovering, formatNumber } = useCharCounter();

  // Computed properties
  const showCounter = computed(() => isHovering.value || charCount.value > props.maxLength! / 2);

  const formattedCharCount = computed(() => formatNumber(charCount.value));

  const formattedMaxLength = computed(() => formatNumber(props.maxLength!));

  const statusColor = computed(() => {
    const percentage = charCount.value / props.maxLength!;
    if (percentage < 0.8) return 'bg-emerald-400 dark:bg-emerald-500';
    if (percentage < 0.95) return 'bg-amber-400 dark:bg-amber-500';
    return 'bg-red-400 dark:bg-red-500';
  });

  // Watch for changes to emit updates
  watch(content, (newContent) => {
    emit('update:content', newContent);
  });

  defineExpose({ clearTextarea });
</script>

<template>
  <div class="relative">
    <!-- <div
      id="textarea-focus-announcement"
      aria-live="polite"
      class="sr-only">
      {{ $t('focus-is-now-in-the-main-text-area') }}
    </div> -->
    <!-- prettier-ignore-attribute class -->
    <textarea
      ref="textareaRef"
      v-model="content"
      :disabled="disabled"
      @input="checkContentLength"
      :maxlength="maxLength"
      :class="[cornerClass]"
      :style="{ minHeight: minHeight + 'px', maxHeight: maxHeight + 'px' }"
      class="
        block w-full resize-none
        rounded-lg border border-gray-200 p-4
        font-system text-base leading-relaxed
        text-gray-900 transition-all
        duration-200 placeholder:text-gray-400 focus:border-brandcomp-500
        focus:ring-2 focus:ring-brandcomp-500
        disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-700 dark:bg-slate-800
        dark:text-white dark:placeholder:text-gray-500
        dark:focus:border-brandcomp-400 dark:focus:ring-brandcomp-400
      "
      :placeholder="$t('web.COMMON.secret_placeholder')"
      :aria-label="$t('enter-the-secret-content-here')">
    </textarea>

    <!--
      Generally speaking, v-if has higher toggle costs while v-show has higher
      initial render costs. So prefer v-show if you need to toggle something
      very often, and prefer v-if if the condition is unlikely to change at
      runtime. -- https://vuejs.org/guide/essentials/conditional.html#v-if-vs-v-show
    -->
    <!-- prettier-ignore-attribute class -->
    <div
      v-if="showCounter"
      class="pointer-events-none absolute bottom-3 right-3 flex select-none items-center gap-1.5
        rounded-full bg-white/90 px-3 py-1 text-sm shadow-sm
        backdrop-blur-sm transition-colors duration-200 dark:bg-gray-800/90">
      <span
        :class="[statusColor, 'size-2.5 rounded-full']"
        aria-hidden="true"></span>
      <span class="font-medium text-gray-600 dark:text-gray-400">
        {{ formattedCharCount }} / {{ formattedMaxLength }}
      </span>
    </div>
  </div>
</template>
